import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import HttpsIcon from '@mui/icons-material/Https';
import MapIcon from '@mui/icons-material/Map';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BlockIcon from '@mui/icons-material/Block';
import { useSiteStore } from '../../store/site.store';
import { usePillarDetail } from '../../hooks/usePillarDetail';
import { activationService } from '../../services/activationService';
import { ActivationEventType } from '../../types/mode.types';

const PILLAR_ID = 'technical_hygiene';

// Helper to safely read a number from rawMetrics
const getNum = (raw: Record<string, unknown> | undefined, key: string): number => {
  if (!raw) return 0;
  const v = raw[key];
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const getBool = (raw: Record<string, unknown> | undefined, key: string, fallback = false): boolean => {
  if (!raw) return fallback;
  const v = raw[key];
  if (v === null || v === undefined) return fallback;
  return Boolean(v);
};

interface SignalCardProps {
  title: string;
  icon: React.ReactNode;
  status: 'PASS' | 'FAIL' | 'INFO';
  headline: string;
  details: { label: string; value: string | number }[];
}

const statusColor = (s: 'PASS' | 'FAIL' | 'INFO') =>
  s === 'PASS' ? 'success.main' : s === 'FAIL' ? 'error.main' : 'info.main';

const StatusIcon: React.FC<{ status: 'PASS' | 'FAIL' | 'INFO' }> = ({ status }) => {
  if (status === 'PASS') return <CheckCircleIcon color="success" />;
  if (status === 'FAIL') return <ErrorIcon color="error" />;
  return <InfoIcon color="info" />;
};

const SignalCard: React.FC<SignalCardProps> = ({ title, icon, status, headline, details }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderTop: 3,
      borderTopColor: statusColor(status),
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box color={statusColor(status)}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Box ml="auto">
          <Tooltip title={status}>
            <span>
              <StatusIcon status={status} />
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 36 }}>
        {headline}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {details.map((d) => (
        <Box key={d.label} display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {d.label}
          </Typography>
          <Typography variant="caption" fontWeight="bold">
            {d.value}
          </Typography>
        </Box>
      ))}
    </CardContent>
  </Card>
);

const TechnicalCrawlingPillarPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  const { data: pillar, isLoading, error } = usePillarDetail(siteId, PILLAR_ID);

  useEffect(() => {
    if (selectedSite?.id && pillar) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService
        .trackEvent(selectedSite.id, {
          eventType: ActivationEventType.READINESS_PILLAR_VIEWED,
          sessionId,
          metadata: { pillarId: PILLAR_ID, pillarName: pillar.pillarName, score: pillar.score, viewType: 'detail' },
        })
        .catch(() => {});
    }
  }, [selectedSite?.id, pillar]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pillar) {
    return <Alert severity="warning">Could not load On-Page Technical SEO data for this site.</Alert>;
  }

  const raw = pillar.rawMetrics;

  // --- HTTPS signal ---
  const totalPages = getNum(raw, 'totalPages');
  const httpsPages = getNum(raw, 'httpsPages');
  const httpPages = getNum(raw, 'httpPages');
  const invalidSsl = getNum(raw, 'invalidSslPages');
  const mixedContent = getNum(raw, 'mixedContentPages');
  const httpsRate = totalPages > 0 ? Math.round((httpsPages / totalPages) * 100) : 0;
  const httpsStatus: 'PASS' | 'FAIL' | 'INFO' =
    invalidSsl > 0 || httpPages > totalPages * 0.2 ? 'FAIL' : httpPages > 0 || mixedContent > 0 ? 'FAIL' : totalPages === 0 ? 'INFO' : 'PASS';

  // --- Sitemap signal ---
  const totalSitemaps = getNum(raw, 'totalSitemaps');
  const sitemapExists = getBool(raw, 'sitemapExists', totalSitemaps > 0);
  const discoveredSitemaps = getNum(raw, 'discoveredSitemaps') || totalSitemaps;
  const sitemapStatus: 'PASS' | 'FAIL' | 'INFO' = sitemapExists || totalSitemaps > 0 ? 'PASS' : 'FAIL';

  // --- Robots.txt signal ---
  const robotsExists = getBool(raw, 'robotsTxtExists');
  const robotsValid = getBool(raw, 'robotsTxtValid', true);
  const criticalBlocked = getNum(raw, 'criticalBlockedUrls');
  const robotsConflict = getNum(raw, 'urlsRobotsConflict');
  const robotsStatus: 'PASS' | 'FAIL' | 'INFO' =
    !robotsExists ? 'INFO' : !robotsValid || criticalBlocked > 0 || robotsConflict > 0 ? 'FAIL' : 'PASS';

  // --- Meta Robots signal ---
  const noindexPages = getNum(raw, 'noindexPages');
  const noindexRate = totalPages > 0 ? Math.round((noindexPages / totalPages) * 100) : 0;
  const metaStatus: 'PASS' | 'FAIL' | 'INFO' =
    noindexPages > totalPages * 0.3 ? 'FAIL' : noindexPages > 0 ? 'INFO' : totalPages === 0 ? 'INFO' : 'PASS';

  const pillarStatus = String(pillar.status);
  const pillarChipColor =
    pillarStatus === 'READY' || pillarStatus === 'EXCELLENT'
      ? 'success'
      : pillarStatus === 'PARTIAL' || pillarStatus === 'NEEDS_WORK'
      ? 'warning'
      : 'error';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/early-stage')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
          <Typography variant="h4" component="h1">
            On-Page Technical SEO
          </Typography>
          <Chip
            label={pillar.status}
            color={pillarChipColor}
          />
        </Box>
        <Typography variant="body1" color="text.secondary">
          {pillar.summary}
        </Typography>
      </Box>

      {/* Score Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Overall Score
              </Typography>
              <Typography variant="body2">
                Search engines must be able to crawl and access your pages before they can rank. These signals
                directly control discoverability.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h2" fontWeight="bold" color="white">
                {pillar.score ?? '—'}
              </Typography>
              <Typography variant="body2">/ 100</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 4 Signal Cards */}
      <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
        Site-Level Signals
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* HTTPS */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="HTTPS & Security"
            icon={<HttpsIcon />}
            status={httpsStatus}
            headline={
              totalPages === 0
                ? 'No pages crawled yet'
                : `${httpsRate}% of pages on HTTPS`
            }
            details={[
              { label: 'HTTPS pages', value: httpsPages },
              { label: 'HTTP pages', value: httpPages },
              { label: 'Invalid SSL', value: invalidSsl },
              { label: 'Mixed content', value: mixedContent },
            ]}
          />
        </Grid>

        {/* Sitemap */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="Sitemap Existence"
            icon={<MapIcon />}
            status={sitemapStatus}
            headline={
              sitemapStatus === 'FAIL'
                ? 'No discovered sitemap configured'
                : `${discoveredSitemaps} discovered sitemap(s) configured`
            }
            details={[
              { label: 'Sitemap exists', value: sitemapStatus === 'PASS' ? 'Yes' : 'No' },
              { label: 'Discovered sitemaps', value: discoveredSitemaps },
              { label: 'Source', value: 'DISCOVERED' },
            ]}
          />
        </Grid>

        {/* Robots.txt */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="Robots.txt"
            icon={<SmartToyIcon />}
            status={robotsStatus}
            headline={
              !robotsExists
                ? 'File not found'
                : !robotsValid
                ? 'Syntax errors detected'
                : 'Accessible and valid'
            }
            details={[
              { label: 'File exists', value: robotsExists ? 'Yes' : 'No' },
              { label: 'Valid', value: robotsValid ? 'Yes' : 'No' },
              { label: 'Critical blocked', value: criticalBlocked },
              { label: 'Sitemap conflicts', value: robotsConflict },
            ]}
          />
        </Grid>

        {/* Meta Robots */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="Meta Robots"
            icon={<BlockIcon />}
            status={metaStatus}
            headline={
              totalPages === 0
                ? 'No pages crawled yet'
                : noindexPages === 0
                ? 'No noindex pages detected'
                : `${noindexPages} page(s) with noindex (${noindexRate}%)`
            }
            details={[
              { label: 'Total pages', value: totalPages },
              { label: 'Noindex pages', value: noindexPages },
              { label: 'Noindex %', value: `${noindexRate}%` },
              { label: 'Error pages (4xx/5xx)', value: getNum(raw, 'errorPages') },
            ]}
          />
        </Grid>
      </Grid>

    </Box>
  );
};

export default TechnicalCrawlingPillarPage;
